/**
 * DeepExplorerActivityPeek — collapsible frame for the existing
 * `DeepExplorerSidePanel` content, used as the right-column "activity peek"
 * inside the new WorkspaceLayout (Phase 4.3b).
 *
 *   collapsed (~64px)        expanded (~440px)
 *   ┌──┐                     ┌────────────────┐
 *   │⚡│  ← icon + badge      │ < (collapse)   │
 *   │ 7│                     │                │
 *   └──┘                     │   <full panel> │
 *                            └────────────────┘
 *
 * The wrapper is intentionally thin: it owns nothing but the collapse/expand
 * frame. All actual panel content (Activity log, Plan, Files, Sources rail)
 * is rendered by `<DeepExplorerPanelContent>` which is driven by the AI
 * Explorer store. Callbacks are passed through from the parent so the same
 * ones wired up for the legacy side-panel work here too.
 *
 * Spec: `.workspace-spec/WORKSPACE_CONTRACT.md` (workspace-mode redesign)
 */
import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PlanDecision } from "@/features/ai-explorer/components/deep-explorer-side-panel/plan-decision-types"
import type { DeepTodo } from "@/stores/ai-explorer-store"

import { ChevronRight, X as XIcon, Zap } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DeepExplorerPanelContent,
  DeepExplorerPanelTitle,
} from "@/features/ai-explorer/components/deep-explorer-side-panel/DeepExplorerSidePanel"
import { cn } from "@/lib/utils"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"

export interface DeepExplorerActivityPeekProps {
  sessionType: string
  sessionTitle?: string
  citationSection?: string
  /** Default expanded state. Defaults to `false` (collapsed strip). */
  defaultExpanded?: boolean
  onPlanDecision?: (
    decision: PlanDecision,
    extra?: { todos?: DeepTodo[]; feedback?: string; editedContent?: string }
  ) => void
  onAskUserResponse?: (
    answers: Record<string, string | string[]>,
    targetSessionId?: string | null,
    askId?: string | null
  ) => void
  onRewindToPlan?: () => void
  onSendUserNote?: (note: string) => boolean
  onApproveInBackground?: (extra?: { editedContent?: string }) => void
  /**
   * Phase 5.2: scope the activity log to a single mission. Set by the
   * Workspace layout when the user clicks "View trace" on an artifact.
   *
   * Today `mission_id ≈ thread_id` (see WORKSPACE_CONTRACT.md, trace_ref),
   * which means the filter is essentially a no-op for now — every event in
   * the current session already belongs to the artifact's mission. The
   * banner + clear affordance still ships so the UX is in place for when
   * mission_id becomes a per-turn UUID.
   */
  missionFilter?: string | null
  /** Clear the trace focus (banner's "Return to live" button). */
  onClearMissionFilter?: () => void
  className?: string
}

// Activity-peek expanded width is user-resizable via a left-edge drag handle.
// Bounds: 320 px (still readable) → 800 px (won't crowd the workspace on a
// 1280-wide laptop). The chosen width persists per browser.
const PEEK_MIN_WIDTH = 320
const PEEK_MAX_WIDTH = 800
const PEEK_DEFAULT_WIDTH = 440
const PEEK_WIDTH_KEY = "hello-ai:workspace-activity-peek-width"

function clampPeekWidth(value: number): number {
  if (!Number.isFinite(value)) return PEEK_DEFAULT_WIDTH
  return Math.min(PEEK_MAX_WIDTH, Math.max(PEEK_MIN_WIDTH, Math.round(value)))
}

function readPersistedPeekWidth(): number {
  if (typeof window === "undefined") return PEEK_DEFAULT_WIDTH
  try {
    const raw = window.localStorage.getItem(PEEK_WIDTH_KEY)
    if (!raw) return PEEK_DEFAULT_WIDTH
    const parsed = Number.parseInt(raw, 10)
    return clampPeekWidth(parsed)
  } catch {
    return PEEK_DEFAULT_WIDTH
  }
}

export function DeepExplorerActivityPeek({
  sessionType,
  sessionTitle,
  citationSection,
  defaultExpanded = false,
  onPlanDecision,
  onAskUserResponse,
  onRewindToPlan,
  onSendUserNote,
  onApproveInBackground,
  missionFilter,
  onClearMissionFilter,
  className,
}: DeepExplorerActivityPeekProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [expandedWidth, setExpandedWidth] = useState<number>(readPersistedPeekWidth)
  // Mirror the latest width into a ref so the pointerup persist closure
  // sees the up-to-date value without re-binding listeners on every change.
  const widthRef = useRef(expandedWidth)
  widthRef.current = expandedWidth

  const handleResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = widthRef.current
    const onMove = (mv: PointerEvent) => {
      // Drag handle is on the LEFT edge of the peek — moving the pointer
      // left grows the panel, right shrinks it.
      const next = clampPeekWidth(startWidth - (mv.clientX - startX))
      setExpandedWidth(next)
    }
    const onUp = () => {
      try {
        window.localStorage.setItem(PEEK_WIDTH_KEY, String(widthRef.current))
      } catch {
        /* storage quota or disabled — fall through */
      }
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
      document.removeEventListener("pointercancel", onUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    document.addEventListener("pointercancel", onUp)
    // Lock the cursor + suppress text selection while dragging — otherwise
    // sweeping the mouse over text mid-drag selects it.
    document.body.style.cursor = "ew-resize"
    document.body.style.userSelect = "none"
  }, [])

  // Phase 5.2: when the user clicks "View trace" on an artifact, the
  // workspace flips `missionFilter` from null → set. Force the peek open so
  // the user actually sees the filtered timeline and the banner — clicking
  // a trace link with the panel collapsed would otherwise be silent.
  useEffect(() => {
    if (missionFilter) setExpanded(true)
  }, [missionFilter])

  // Live event count for the collapsed-strip badge. We read from the
  // namespaced AI Explorer store so the badge updates as new activity
  // frames stream in — no manual refresh required.
  const explorerStore = useAIExplorerStore(sessionType)
  const eventCount = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).activityLog.length
  )

  const expandLabel = t("deepExplorer.expand_activity", {
    defaultValue: "Expand activity",
  })
  const countLabel = t("deepExplorer.activity_count", {
    defaultValue: "{{count}} activity events",
    count: eventCount,
  })

  if (!expanded) {
    return (
      <div
        data-testid="workspace-activity-peek-collapsed"
        className={cn(
          "bg-background flex h-full w-16 flex-col items-center gap-2 border-l py-3",
          className
        )}
      >
        <Button
          aria-label={expandLabel}
          className="relative size-9 rounded-full"
          data-testid="workspace-activity-peek-expand"
          size="icon"
          title={countLabel}
          type="button"
          variant="ghost"
          onClick={() => setExpanded(true)}
        >
          <Zap aria-hidden className="size-5" />
          {eventCount > 0 ? (
            <span
              aria-hidden
              className="border-foreground bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 min-w-4 rounded-full border px-1 text-[10px] leading-4 tabular-nums"
              data-testid="workspace-activity-peek-badge"
            >
              {eventCount}
            </span>
          ) : null}
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn("bg-background relative flex h-full min-w-0 flex-col border-l", className)}
      data-testid="workspace-activity-peek-expanded"
      style={{ width: `${expandedWidth}px` }}
    >
      {/* W3C window-splitter pattern: a focusable resize handle with role=separator + aria-orientation
          IS interactive per ARIA. jsx-a11y treats role=separator as non-interactive, so disable here. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        aria-orientation="vertical"
        aria-valuemax={PEEK_MAX_WIDTH}
        aria-valuemin={PEEK_MIN_WIDTH}
        aria-valuenow={expandedWidth}
        className="hover:bg-primary/30 active:bg-primary/40 absolute top-0 left-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-ew-resize touch-none transition-colors"
        data-testid="workspace-activity-peek-resize-handle"
        role="separator"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- separator role is treated non-interactive but the W3C resize-handle pattern requires it be focusable
        tabIndex={0}
        aria-label={t("deepExplorer.activity_peek_resize", {
          defaultValue: "Resize activity panel",
        })}
        onPointerDown={handleResizeStart}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault()
            setExpandedWidth((w) => clampPeekWidth(w + 16))
          } else if (e.key === "ArrowRight") {
            e.preventDefault()
            setExpandedWidth((w) => clampPeekWidth(w - 16))
          }
        }}
      />
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="min-w-0 flex-1 truncate text-sm font-medium">
          <DeepExplorerPanelTitle sessionType={sessionType} />
        </div>
        <Button
          className="size-7 shrink-0"
          data-testid="workspace-activity-peek-collapse"
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("deepExplorer.collapse_activity", {
            defaultValue: "Collapse activity",
          })}
          onClick={() => setExpanded(false)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      {missionFilter ? (
        <div
          className="border-primary/30 bg-primary/5 flex items-center justify-between gap-2 border-b px-3 py-1.5 text-[11px]"
          data-testid="workspace-activity-peek-trace-banner"
          role="status"
        >
          <span className="text-foreground min-w-0 flex-1 truncate">
            {t("deepExplorer.trace_filter_banner", {
              defaultValue: "Showing trace for mission {{mission}}",
              mission: missionFilter.slice(0, 8),
            })}
          </span>
          {onClearMissionFilter ? (
            <Button
              className="h-6 shrink-0 gap-1 px-2 text-[11px]"
              data-testid="workspace-activity-peek-clear-trace"
              size="sm"
              type="button"
              variant="ghost"
              aria-label={t("deepExplorer.trace_filter_return_to_live", {
                defaultValue: "Return to live",
              })}
              onClick={onClearMissionFilter}
            >
              <XIcon aria-hidden className="size-3" />
              <span>
                {t("deepExplorer.trace_filter_return_to_live", {
                  defaultValue: "Return to live",
                })}
              </span>
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        <DeepExplorerPanelContent
          inWorkspaceMode
          citationSection={citationSection}
          missionFilter={missionFilter}
          sessionTitle={sessionTitle}
          sessionType={sessionType}
          onApproveInBackground={onApproveInBackground}
          onAskUserResponse={onAskUserResponse}
          onPlanDecision={onPlanDecision}
          onRewindToPlan={onRewindToPlan}
          onSendUserNote={onSendUserNote}
        />
      </div>
    </div>
  )
}
